import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  Circle,
  ArrowRight,
  Clock,
  CreditCard,
  AlertCircle,
  Lock,
  Loader,
} from "lucide-react";
import catlogService from "../../services/catlogService";
import PaymentModal from "./PaymentModal";
import { useAuth } from "../../context/AuthContext";

const OverviewSection = ({ overview, profile, onRefresh, onNavigate }) => {
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxPollAttempts = 60;
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const pollPaymentStatus = async (reqId) => {
    try {
      const response = await catlogService.getStudyPaymentStatus(reqId);

      if (response.data.status === "completed" && response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
        return true;
      } else if (response.data.status === "failed") {
        setIsProcessing(false);
        setErrorMessage("Something went wrong");
        return true;
      }

      return false;
    } catch (error) {
      console.error("Poll error:", error);
      return false;
    }
  };

  const startPolling = async (reqId) => {
    setPollAttempts(0);

    const poll = async () => {
      if (pollAttempts >= maxPollAttempts) {
        setIsProcessing(false);
        setErrorMessage("Something went wrong");
        return;
      }

      const completed = await pollPaymentStatus(reqId);

      if (!completed) {
        setPollAttempts((prev) => prev + 1);
        setTimeout(poll, 5000);
      }
    };

    poll();
  };

  const handlePayNow = async () => {
    setIsProcessing(true);
    setErrorMessage("");

    try {
      const createResponse = await catlogService.createStudyPaymentRequest();
      const reqId = createResponse.data.requestId;

      await catlogService.automateStudyPayment(reqId);
      startPolling(reqId);
    } catch (error) {
      setIsProcessing(false);
      setErrorMessage("Something went wrong");
    }
  };

  if (!overview || !profile) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  const getPipelineStatusDisplay = (status) => {
    const statusMap = {
      consultation_scheduled: {
        label: "Consultation Scheduled",
        color: "blue",
      },
      consultation_completed: {
        label: "Consultation Completed",
        color: "green",
      },
      onboarding: { label: "Onboarding", color: "blue" },
      payment_pending: { label: "Payment Pending", color: "yellow" },
      documents_pending: { label: "Documents Pending", color: "yellow" },
      sop_pending: { label: "SOP Pending", color: "yellow" },
      applications_in_progress: {
        label: "Applications In Progress",
        color: "blue",
      },
      awaiting_offers: { label: "Awaiting Offers", color: "purple" },
      offer_received: { label: "Offer Received", color: "green" },
      enrolled: { label: "Enrolled", color: "green" },
      completed: { label: "Completed", color: "green" },
    };

    return statusMap[status] || { label: status, color: "gray" };
  };

  const statusDisplay = getPipelineStatusDisplay(profile.pipelineStatus);
  const statusColors = {
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    green: "bg-green-100 text-green-700 border-green-200",
    yellow: "bg-yellow-100 text-yellow-700 border-yellow-200",
    purple: "bg-purple-100 text-purple-700 border-purple-200",
    gray: "bg-gray-100 text-gray-700 border-gray-200",
  };

  return (
    <div className="p-6 lg:p-8">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-2xl font-bold text-[#202356] mb-2">
          Hello{user?.firstName ? `, ${user.firstName}` : ""}!
        </h1>
        <p className="text-gray-600">
          Track your progress and complete each section to move forward
        </p>
      </motion.div>

      {/* Current Status Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="mt-6 bg-white rounded-lg border border-gray-200 p-6 shadow-sm"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Current Status
          </h2>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium border ${statusColors[statusDisplay.color]}`}
          >
            {statusDisplay.label}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#F5F8FA] rounded-lg flex items-center justify-center">
              <Clock className="text-[#202356]" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Progress</p>
              <p className="text-xl font-semibold text-gray-900">
                {overview.progressPercentage}%
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                overview.paymentStatus === "paid"
                  ? "bg-green-100"
                  : "bg-[#F5F8FA]"
              }`}
            >
              <CreditCard
                className={
                  overview.paymentStatus === "paid"
                    ? "text-green-600"
                    : "text-[#202356]"
                }
                size={24}
              />
            </div>
            <div>
              <p className="text-sm text-gray-600">Payment</p>
              <p className="text-xl font-semibold text-gray-900 capitalize">
                {overview.paymentStatus}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#F5F8FA] rounded-lg flex items-center justify-center">
              <CheckCircle className="text-[#202356]" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Sections Done</p>
              <p className="text-xl font-semibold text-gray-900">
                {
                  Object.values(overview.sectionsCompleted).filter(Boolean)
                    .length
                }{" "}
                / 5
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Payment CTA for unpaid users - only show after completing first 3 sections */}
      {overview.paymentStatus !== "paid" &&
        overview.sectionsCompleted?.personalDetails &&
        overview.sectionsCompleted?.academicHistory &&
        overview.sectionsCompleted?.programPreferences && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-6"
          >
            <div className="bg-gradient-to-r from-[#202356] to-[#2542D4] rounded-lg p-6 text-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Lock size={20} />
                    <h3 className="text-lg font-semibold">
                      Complete Your Application Journey
                    </h3>
                  </div>
                  <p className="text-sm text-blue-100 mb-4">
                    Unlock document uploads and personalized SOP assistance
                  </p>
                  <p className="text-xs text-gray-300 text-opacity-90 mb-4">
                    We'll manage your complete application from start to finish,
                    beginning the moment you unlock access. Share your documents
                    and story with us, and we'll craft a compelling Statement of
                    Purpose that highlights your unique strengths and
                    aspirations. Through our direct relationships with academic
                    institutions, we understand what each admissions committee
                    is looking for and will position your application
                    accordingly.
                  </p>
                  <ul className="space-y-2 text-xs text-gray-300 text-opacity-90 mb-4">
                    <li className="flex items-center gap-2">
                      <CheckCircle size={14} />
                      <span>Upload documents to start your application</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={14} />
                      <span>We'll create your expert SOP</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={14} />
                      <span>Benefit from our institutional insights</span>
                    </li>
                  </ul>

                  {/* Error Message */}
                  {errorMessage && (
                    <div className="mb-4 p-3 bg-red-500 bg-opacity-20 border border-red-300 rounded-lg flex items-start gap-2">
                      <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-white">{errorMessage}</p>
                    </div>
                  )}

                  <button
                    onClick={handlePayNow}
                    disabled={isProcessing}
                    className="bg-white text-[#202356] font-semibold px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? (
                      <>
                        <Loader className="animate-spin" size={20} />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <CreditCard size={20} />
                        <span>Pay $149 Now</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

      {/* Next Steps */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mt-6"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Next Steps</h2>
        <div className="space-y-3">
          {overview.nextSteps && overview.nextSteps.length > 0 ? (
            overview.nextSteps.map((step, index) => (
              <motion.button
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                onClick={() => {
                  if (step.section === "payment") {
                    setShowPaymentModal(true);
                  } else {
                    onNavigate && onNavigate(step.section);
                  }
                }}
                className="w-full bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-[#202356] transition-all text-left"
              >
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#202356] text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                  <ArrowRight
                    className="text-[#202356] flex-shrink-0"
                    size={20}
                  />
                </div>
              </motion.button>
            ))
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle
                className="text-green-600 flex-shrink-0 mt-0.5"
                size={20}
              />
              <div>
                <h3 className="font-semibold text-green-900 mb-1">All Set!</h3>
                <p className="text-sm text-green-700">
                  Your profile is complete. Our team will contact you via email
                  with next steps.
                </p>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Section Completion Checklist */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mt-6"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Section Checklist
        </h2>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="space-y-3">
            {[
              { key: "personalDetails", label: "Personal Details", free: true },
              { key: "academicHistory", label: "Academic History", free: true },
              {
                key: "programmePreferences",
                label: "Programme Preferences",
                free: true,
              },
              { key: "documents", label: "Documents", free: false },
              { key: "sop", label: "Statement of Purpose", free: false },
            ].map((section) => {
              const isCompleted = overview.sectionsCompleted[section.key];
              const isLocked =
                !section.free && overview.paymentStatus !== "paid";

              return (
                <div
                  key={section.key}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    isCompleted
                      ? "bg-green-50"
                      : isLocked
                        ? "bg-gray-50"
                        : "bg-blue-50"
                  }`}
                >
                  {isCompleted ? (
                    <CheckCircle className="text-green-600" size={20} />
                  ) : (
                    <Circle
                      className={isLocked ? "text-gray-400" : "text-blue-600"}
                      size={20}
                    />
                  )}
                  <span
                    className={`flex-1 font-medium ${
                      isCompleted
                        ? "text-green-900"
                        : isLocked
                          ? "text-gray-500"
                          : "text-blue-900"
                    }`}
                  >
                    {section.label}
                  </span>
                  {!section.free && overview.paymentStatus !== "paid" && (
                    <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                      Payment Required
                    </span>
                  )}
                  {isCompleted && (
                    <span className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
                      Completed
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Payment Notice (if not paid) */}
      {overview.paymentStatus !== "paid" &&
        overview.hasCompletedFreeSection && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6"
          >
            <div className="flex items-start gap-3">
              <AlertCircle
                className="text-yellow-600 flex-shrink-0 mt-0.5"
                size={24}
              />
              <div className="flex-1">
                <h3 className="font-semibold text-yellow-900 mb-2">
                  Payment Required
                </h3>
                <p className="text-sm text-yellow-800 mb-4">
                  Complete the free sections to unlock document upload and
                  interview scheduling. Payment is required to access these
                  premium features.
                </p>

                {/* Error Message */}
                {errorMessage && (
                  <div className="mb-4 p-3 bg-red-500 bg-opacity-20 border border-red-300 rounded-lg flex items-start gap-2">
                    <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-900">{errorMessage}</p>
                  </div>
                )}

                <button
                  onClick={handlePayNow}
                  disabled={isProcessing}
                  className="bg-[#202356] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#2542D4] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <>
                      <Loader className="animate-spin" size={20} />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <CreditCard size={20} />
                      <span>Pay $149 Now</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}

      {/* Help Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="mt-6 bg-white rounded-lg border border-gray-200 p-6"
      >
        <h3 className="font-semibold text-gray-900 mb-2">Need Help?</h3>
        <p className="text-sm text-gray-600 mb-4">
          If you have any questions or need assistance, our team is here to
          help.
        </p>
        <div className="flex flex-wrap gap-3">
          <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
            Contact Support
          </button>
          <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
            View FAQs
          </button>
        </div>
      </motion.div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
      />
    </div>
  );
};

export default OverviewSection;

// import React, { useState } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import {
//   CheckCircle,
//   Circle,
//   ArrowRight,
//   Clock,
//   CreditCard,
//   AlertCircle,
//   Lock,
//   Loader
// } from 'lucide-react';
// import studyAbroadService from '../../services/studyAbroadService';
// import PaymentModal from './PaymentModal';
// import { useAuth } from '../../context/AuthContext';

// const OverviewSection = ({ overview, profile, onRefresh, onNavigate }) => {
//   const { user } = useAuth();
//   const [isProcessing, setIsProcessing] = useState(false);
//   const [errorMessage, setErrorMessage] = useState('');
//   const [pollAttempts, setPollAttempts] = useState(0);
//   const maxPollAttempts = 60;
//   const [showPaymentModal, setShowPaymentModal] = useState(false);

//   const pollPaymentStatus = async (reqId) => {
//     try {
//       const response = await studyAbroadService.getPaymentStatus(reqId);

//       if (response.data.status === 'completed' && response.data.checkoutUrl) {
//         window.location.href = response.data.checkoutUrl;
//         return true;
//       } else if (response.data.status === 'failed') {
//         setIsProcessing(false);
//         setErrorMessage(response.data.errorMessage || 'Something went wrong');
//         return true;
//       }

//       return false;
//     } catch (error) {
//       console.error('Poll error:', error);
//       return false;
//     }
//   };

//   const startPolling = async (reqId) => {
//     setPollAttempts(0);

//     const poll = async () => {
//       if (pollAttempts >= maxPollAttempts) {
//         setIsProcessing(false);
//         setErrorMessage('Something went wrong');
//         return;
//       }

//       const completed = await pollPaymentStatus(reqId);

//       if (!completed) {
//         setPollAttempts(prev => prev + 1);
//         setTimeout(poll, 5000);
//       }
//     };

//     poll();
//   };

//   const handlePayNow = async () => {
//     setIsProcessing(true);
//     setErrorMessage('');

//     try {
//       const createResponse = await studyAbroadService.createPaymentRequest();
//       const reqId = createResponse.data.requestId;

//       await studyAbroadService.automatePayment(reqId);
//       startPolling(reqId);
//     } catch (error) {
//       setIsProcessing(false);
//       setErrorMessage('Something went wrong');
//     }
//   };

//   if (!overview || !profile) {
//     return (
//       <div className="p-6">
//         <div className="animate-pulse space-y-4">
//           <div className="h-8 bg-gray-200 rounded w-1/3"></div>
//           <div className="h-4 bg-gray-200 rounded w-2/3"></div>
//         </div>
//       </div>
//     );
//   }

//   const getPipelineStatusDisplay = (status) => {
//     const statusMap = {
//       'consultation_scheduled': { label: 'Consultation Scheduled', color: 'blue' },
//       'consultation_completed': { label: 'Consultation Completed', color: 'green' },
//       'onboarding': { label: 'Onboarding', color: 'blue' },
//       'payment_pending': { label: 'Payment Pending', color: 'yellow' },
//       'documents_pending': { label: 'Documents Pending', color: 'yellow' },
//       'sop_pending': { label: 'SOP Pending', color: 'yellow' },
//       'applications_in_progress': { label: 'Applications In Progress', color: 'blue' },
//       'awaiting_offers': { label: 'Awaiting Offers', color: 'purple' },
//       'offer_received': { label: 'Offer Received', color: 'green' },
//       'enrolled': { label: 'Enrolled', color: 'green' },
//       'completed': { label: 'Completed', color: 'green' }
//     };

//     return statusMap[status] || { label: status, color: 'gray' };
//   };

//   const statusDisplay = getPipelineStatusDisplay(profile.pipelineStatus);
//   const statusColors = {
//     blue: 'bg-blue-100 text-blue-700 border-blue-200',
//     green: 'bg-green-100 text-green-700 border-green-200',
//     yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
//     purple: 'bg-purple-100 text-purple-700 border-purple-200',
//     gray: 'bg-gray-100 text-gray-700 border-gray-200'
//   };

//   return (
//     <div className="p-6 lg:p-8">
//       {/* Welcome Header */}
//       <motion.div
//         initial={{ opacity: 0, y: 20 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 0.5 }}
//       >
//         <h1 className="text-2xl font-bold text-[#202356] mb-2">
//           Hello{user?.firstName ? `, ${user.firstName}` : ''}!
//         </h1>
//         <p className="text-gray-600">
//           Track your progress and complete each section to move forward
//         </p>
//       </motion.div>

//       {/* Current Status Card */}
//       <motion.div
//         initial={{ opacity: 0, y: 20 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 0.5, delay: 0.1 }}
//         className="mt-6 bg-white rounded-lg border border-gray-200 p-6 shadow-sm"
//       >
//         <div className="flex items-center justify-between mb-4">
//           <h2 className="text-lg font-semibold text-gray-900">Current Status</h2>
//           <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusColors[statusDisplay.color]}`}>
//             {statusDisplay.label}
//           </span>
//         </div>

//         <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//           <div className="flex items-center gap-3">
//             <div className="w-12 h-12 bg-[#F5F8FA] rounded-lg flex items-center justify-center">
//               <Clock className="text-[#202356]" size={24} />
//             </div>
//             <div>
//               <p className="text-sm text-gray-600">Progress</p>
//               <p className="text-xl font-semibold text-gray-900">{overview.progressPercentage}%</p>
//             </div>
//           </div>

//           <div className="flex items-center gap-3">
//             <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
//               overview.paymentStatus === 'paid' ? 'bg-green-100' : 'bg-[#F5F8FA]'
//             }`}>
//               <CreditCard className={overview.paymentStatus === 'paid' ? 'text-green-600' : 'text-[#202356]'} size={24} />
//             </div>
//             <div>
//               <p className="text-sm text-gray-600">Payment</p>
//               <p className="text-xl font-semibold text-gray-900 capitalize">{overview.paymentStatus}</p>
//             </div>
//           </div>

//           <div className="flex items-center gap-3">
//             <div className="w-12 h-12 bg-[#F5F8FA] rounded-lg flex items-center justify-center">
//               <CheckCircle className="text-[#202356]" size={24} />
//             </div>
//             <div>
//               <p className="text-sm text-gray-600">Sections Done</p>
//               <p className="text-xl font-semibold text-gray-900">
//                 {Object.values(overview.sectionsCompleted).filter(Boolean).length} / 5
//               </p>
//             </div>
//           </div>
//         </div>
//       </motion.div>

//       {/* Payment CTA for unpaid users - only show after completing first 3 sections */}
//       {overview.paymentStatus !== 'paid' &&
//        overview.sectionsCompleted?.personalDetails &&
//        overview.sectionsCompleted?.academicHistory &&
//        overview.sectionsCompleted?.programPreferences && (
//         <motion.div
//           initial={{ opacity: 0, y: 20 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.5, delay: 0.15 }}
//           className="mt-6"
//         >
//           <div className="bg-gradient-to-r from-[#202356] to-[#2542D4] rounded-lg p-6 text-white">
//             <div className="flex items-start justify-between">
//               <div className="flex-1">
//                 <div className="flex items-center gap-2 mb-2">
//                   <Lock size={20} />
//                   <h3 className="text-lg font-semibold">Complete Your Application Journey</h3>
//                 </div>
//                 <p className="text-sm text-blue-100 mb-4">
//                   Unlock document uploads and personalized SOP assistance
//                 </p>
//                 <p className="text-xs text-gray-300 text-opacity-90 mb-4">
//                   We'll manage your complete application from start to finish, beginning the moment you unlock access. Share your documents and story with us, and we'll craft a compelling Statement of Purpose that highlights your unique strengths and aspirations. Through our direct relationships with academic institutions, we understand what each admissions committee is looking for and will position your application accordingly.
//                 </p>
//                 <ul className="space-y-2 text-xs text-gray-300 text-opacity-90 mb-4">
//                   <li className="flex items-center gap-2">
//                     <CheckCircle size={14} />
//                     <span>Upload documents to start your application</span>
//                   </li>
//                   <li className="flex items-center gap-2">
//                     <CheckCircle size={14} />
//                     <span>We'll create your expert SOP</span>
//                   </li>
//                   <li className="flex items-center gap-2">
//                     <CheckCircle size={14} />
//                     <span>Benefit from our institutional insights</span>
//                   </li>
//                 </ul>

//                 {/* Error Message */}
//                 {errorMessage && (
//                   <div className="mb-4 p-3 bg-red-500 bg-opacity-20 border border-red-300 rounded-lg flex items-start gap-2">
//                     <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
//                     <p className="text-sm text-white">{errorMessage}</p>
//                   </div>
//                 )}

//                 <button
//                   onClick={handlePayNow}
//                   disabled={isProcessing}
//                   className="bg-white text-[#202356] font-semibold px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
//                 >
//                   {isProcessing ? (
//                     <>
//                       <Loader className="animate-spin" size={20} />
//                       <span>Processing...</span>
//                     </>
//                   ) : (
//                     <>
//                       <CreditCard size={20} />
//                       <span>Pay $149 Now</span>
//                     </>
//                   )}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </motion.div>
//       )}

//       {/* Next Steps */}
//       <motion.div
//         initial={{ opacity: 0, y: 20 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 0.5, delay: 0.2 }}
//         className="mt-6"
//       >
//         <h2 className="text-lg font-semibold text-gray-900 mb-4">Next Steps</h2>
//         <div className="space-y-3">
//           {overview.nextSteps && overview.nextSteps.length > 0 ? (
//             overview.nextSteps.map((step, index) => (
//               <motion.button
//                 key={index}
//                 initial={{ opacity: 0, x: -20 }}
//                 animate={{ opacity: 1, x: 0 }}
//                 transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
//                 onClick={() => {
//                   if (step.section === 'payment') {
//                     setShowPaymentModal(true);
//                   } else {
//                     onNavigate && onNavigate(step.section);
//                   }
//                 }}
//                 className="w-full bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-[#202356] transition-all text-left"
//               >
//                 <div className="flex items-start gap-4">
//                   <div className="w-8 h-8 bg-[#202356] text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold">
//                     {index + 1}
//                   </div>
//                   <div className="flex-1">
//                     <h3 className="font-semibold text-gray-900 mb-1">{step.title}</h3>
//                     <p className="text-sm text-gray-600">{step.description}</p>
//                   </div>
//                   <ArrowRight className="text-[#202356] flex-shrink-0" size={20} />
//                 </div>
//               </motion.button>
//             ))
//           ) : (
//             <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
//               <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
//               <div>
//                 <h3 className="font-semibold text-green-900 mb-1">All Set!</h3>
//                 <p className="text-sm text-green-700">
//                   Your profile is complete. Our team will contact you via email with next steps.
//                 </p>
//               </div>
//             </div>
//           )}
//         </div>
//       </motion.div>

//       {/* Section Completion Checklist */}
//       <motion.div
//         initial={{ opacity: 0, y: 20 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 0.5, delay: 0.4 }}
//         className="mt-6"
//       >
//         <h2 className="text-lg font-semibold text-gray-900 mb-4">Section Checklist</h2>
//         <div className="bg-white rounded-lg border border-gray-200 p-6">
//           <div className="space-y-3">
//             {[
//               { key: 'personalDetails', label: 'Personal Details', free: true },
//               { key: 'academicHistory', label: 'Academic History', free: true },
//               { key: 'programmePreferences', label: 'Programme Preferences', free: true },
//               { key: 'documents', label: 'Documents', free: false },
//               { key: 'sop', label: 'Statement of Purpose', free: false }
//             ].map((section) => {
//               const isCompleted = overview.sectionsCompleted[section.key];
//               const isLocked = !section.free && overview.paymentStatus !== 'paid';

//               return (
//                 <div
//                   key={section.key}
//                   className={`flex items-center gap-3 p-3 rounded-lg ${
//                     isCompleted ? 'bg-green-50' : isLocked ? 'bg-gray-50' : 'bg-blue-50'
//                   }`}
//                 >
//                   {isCompleted ? (
//                     <CheckCircle className="text-green-600" size={20} />
//                   ) : (
//                     <Circle className={isLocked ? 'text-gray-400' : 'text-blue-600'} size={20} />
//                   )}
//                   <span className={`flex-1 font-medium ${
//                     isCompleted ? 'text-green-900' : isLocked ? 'text-gray-500' : 'text-blue-900'
//                   }`}>
//                     {section.label}
//                   </span>
//                   {!section.free && overview.paymentStatus !== 'paid' && (
//                     <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
//                       Payment Required
//                     </span>
//                   )}
//                   {isCompleted && (
//                     <span className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
//                       Completed
//                     </span>
//                   )}
//                 </div>
//               );
//             })}
//           </div>
//         </div>
//       </motion.div>

//       {/* Payment Notice (if not paid) */}
//       {overview.paymentStatus !== 'paid' && overview.hasCompletedFreeSection && (
//         <motion.div
//           initial={{ opacity: 0, y: 20 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.5, delay: 0.5 }}
//           className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6"
//         >
//           <div className="flex items-start gap-3">
//             <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={24} />
//             <div className="flex-1">
//               <h3 className="font-semibold text-yellow-900 mb-2">Payment Required</h3>
//               <p className="text-sm text-yellow-800 mb-4">
//                 Complete the free sections to unlock document upload and interview scheduling. Payment is required to access these premium features.
//               </p>

//               {/* Error Message */}
//               {errorMessage && (
//                 <div className="mb-4 p-3 bg-red-500 bg-opacity-20 border border-red-300 rounded-lg flex items-start gap-2">
//                   <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
//                   <p className="text-sm text-red-900">{errorMessage}</p>
//                 </div>
//               )}

//               <button
//                 onClick={handlePayNow}
//                 disabled={isProcessing}
//                 className="bg-[#202356] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#2542D4] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
//               >
//                 {isProcessing ? (
//                   <>
//                     <Loader className="animate-spin" size={20} />
//                     <span>Processing...</span>
//                   </>
//                 ) : (
//                   <>
//                     <CreditCard size={20} />
//                     <span>Pay $149 Now</span>
//                   </>
//                 )}
//               </button>
//             </div>
//           </div>
//         </motion.div>
//       )}

//       {/* Help Section */}
//       <motion.div
//         initial={{ opacity: 0, y: 20 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 0.5, delay: 0.6 }}
//         className="mt-6 bg-white rounded-lg border border-gray-200 p-6"
//       >
//         <h3 className="font-semibold text-gray-900 mb-2">Need Help?</h3>
//         <p className="text-sm text-gray-600 mb-4">
//           If you have any questions or need assistance, our team is here to help.
//         </p>
//         <div className="flex flex-wrap gap-3">
//           <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
//             Contact Support
//           </button>
//           <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
//             View FAQs
//           </button>
//         </div>
//       </motion.div>

//       {/* Payment Modal */}
//       <PaymentModal
//         isOpen={showPaymentModal}
//         onClose={() => setShowPaymentModal(false)}
//       />
//     </div>
//   );
// };

// export default OverviewSection;
